import React from 'react';
// Imported (not /public) so the build content-hashes the filename.
import gateArt from '../../assets/bg/gate-login.webp';

/**
 * The sealed gate that Login, Reset and Farewell stand in front of.
 *
 * The name, the crest and the keys are carved into the artwork, so nothing on
 * top repeats them; a visually hidden heading gives a screen reader the name
 * the picture cannot say. Whatever the page puts here starts below the crest
 * (see .gate-seal-clear in index.css) -- the blueprint asks for the seal to be
 * fully unobstructed, and a short screen scrolls rather than covering it.
 *
 * `reserve` is roughly how tall the page's content is, in px, so the artwork
 * can shrink just enough to fit it under the crest without scrolling. A guess
 * that is too small only costs a short scroll; it never covers the seal.
 *
 * `anchor="bottom"` (the default) settles the content at the foot of the
 * screen, where a thumb reaches the form. `anchor="seal"` keeps it just under
 * the crest, for a page with nothing to type.
 *
 * Never the signed-in shell: no navigation, no member data.
 */
const Gate = ({ anchor = 'bottom', reserve = 0, footer, children, ...rest }) => (
  <div
    className="gate relative min-h-screen-dvh overflow-x-hidden bg-background flex flex-col items-center px-4"
    style={{
      '--gate-reserve': `${reserve}px`,
      // Room for the install banner too, so it never hides the last button.
      paddingBottom: 'calc(20px + env(safe-area-inset-bottom) + var(--bottom-overlay, 0px))',
    }}
    {...rest}
  >
    <div className="gate-art" style={{ backgroundImage: `url(${gateArt})` }} aria-hidden="true" />
    <h1 className="sr-only">Arcanum</h1>

    <div className="gate-seal-clear" aria-hidden="true" />
    {anchor === 'bottom' && <div className="flex-1" />}

    <div className="relative w-full max-w-sm">{children}</div>

    {anchor === 'seal' && <div className="flex-1" />}
    {footer && (
      <div className="relative w-full max-w-sm mt-4 text-center text-[11px] leading-relaxed text-muted-foreground">
        {footer}
      </div>
    )}
  </div>
);

export default Gate;
